import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { signupSchema } from "./utils/validation";
import { useRef, useState } from "react";
import "react-international-phone/style.css";
import { data, readableLabels } from "./utils/data";
import PhoneInputComponent from "./PhoneInput";
import useOtp from "./useOtp";
import { Link } from "react-router-dom/cjs/react-router-dom";

function Form() {
  const recaptchaRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(signupSchema),
    mode: "onBlur",
  });

  const { sendOtpFn } = useOtp();

  const onSubmit = (formData) => {
    setLoading(true);
    sendOtpFn({ data: formData, mobile: formData.phone });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#f8fafc] to-[#e0e7ef] py-8 px-4">
      <div className="w-full max-w-5xl bg-white rounded-2xl shadow-2xl px-12 py-8 flex flex-col items-center animate-fade-in">
        {/* Logo */}
        <img src="/zstore_logo.png" alt="Z-Store Logo" className="h-16 mb-6" />
        {/* Headline */}
        <h2 className="text-3xl font-bold text-gray-900 mb-2 text-center">
          Create your Z-Store account
        </h2>
        <p className="text-gray-500 mb-8 text-center text-base">
          Sign up to start shopping and managing your orders.
        </p>
        <form
          className="w-full max-w-2xl flex flex-col gap-8"
          onSubmit={handleSubmit(onSubmit)}
        >
          {data?.map((field) => (
            <div key={field.id} className="flex flex-col">
              {/* Label */}
              <label
                htmlFor={field.name}
                className="text-gray-700 cursor-pointer font-medium mb-2 text-base"
              >
                {readableLabels[field.name]}
              </label>
              {/* Field */}
              {field.name === "phone" ? (
                <div>
                  <PhoneInputComponent
                    field={field}
                    control={control}
                    register={register}
                    errors={errors}
                    showLabel={false}
                  />
                </div>
              ) : (
                <div className="relative">
                  <input
                    {...register(field.name)}
                    id={field.name}
                    type={
                      field.type === "password" && showPassword
                        ? "text"
                        : field.type || "text"
                    }
                    placeholder=" "
                    className={`w-full py-[0.65rem] px-4 border-2 border-gray-200 rounded-lg bg-white text-gray-900 focus:outline-none focus:border-[#da0e1a] transition-colors text-base ${
                      errors[field.name] ? "border-red-400" : ""
                    }`}
                  />
                  {field.type === "password" && (
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute top-1/2 right-4 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width={20}
                          height={20}
                          viewBox="0 0 16 16"
                          fill="currentColor"
                        >
                          <path d="m10.12 10.827l4.026 4.027a.5.5 0 0 0 .708-.708l-13-13a.5.5 0 1 0-.708.708l3.23 3.23A6 6 0 0 0 3.2 6.182a6.7 6.7 0 0 0-1.117 1.982c-.021.061-.047.145-.047.145l-.018.062s-.076.497.355.611a.5.5 0 0 0 .611-.355l.001-.003l.008-.025l.035-.109a5.7 5.7 0 0 1 .945-1.674a5 5 0 0 1 1.124-1.014L6.675 7.38a2.5 2.5 0 1 0 3.446 3.446m-.74-.74A1.5 1.5 0 1 1 7.413 8.12zM6.32 4.2l.854.854Q7.564 5 8 5c2.044 0 3.286.912 4.028 1.817a5.7 5.7 0 0 1 .945 1.674q.025.073.035.109l.008.025v.003l.001.001a.5.5 0 0 0 .966-.257v-.003l-.001-.004l-.004-.013a2 2 0 0 0-.06-.187a6.7 6.7 0 0 0-1.117-1.982C11.905 5.089 10.396 4 8.002 4c-.618 0-1.177.072-1.681.199" />
                        </svg>
                      ) : (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width={20}
                          height={20}
                          viewBox="0 0 16 16"
                          fill="currentColor"
                        >
                          <path d="M8 6.003a2.667 2.667 0 1 1 0 5.334a2.667 2.667 0 0 1 0-5.334m0 1a1.667 1.667 0 1 0 0 3.334a1.667 1.667 0 0 0 0-3.334m0-3.336c3.076 0 5.73 2.1 6.467 5.043a.5.5 0 1 1-.97.242a5.67 5.67 0 0 0-10.995.004a.5.5 0 0 1-.97-.243A6.67 6.67 0 0 1 8 3.667" />
                        </svg>
                      )}
                    </button>
                  )}
                </div>
              )}
              {errors[field.name] && (
                <p className="text-red-500 text-sm mt-2 text-left">
                  {errors[field.name]?.message}
                </p>
              )}
            </div>
          ))}
          <button
            id="sign-up-button"
            className="bg-gradient-to-r from-[#da0e1a] to-[#c40d17] rounded-lg w-full py-3 font-semibold text-xl cursor-pointer transition-colors hover:from-[#c40d17] hover:to-[#da0e1a] mb-2 text-white flex items-center justify-center gap-3"
            disabled={loading}
          >
            {loading && (
              <svg
                className="animate-spin h-6 w-6 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v8z"
                ></path>
              </svg>
            )}
            {loading ? "Signing up..." : "Sign Up"}
          </button>
        </form>
        <div className="flex items-center w-full max-w-2xl my-6">
          <div className="flex-grow border-t border-gray-200"></div>
          <span className="mx-4 text-gray-400 text-sm">or</span>
          <div className="flex-grow border-t border-gray-200"></div>
        </div>
        <div className="flex items-center justify-center gap-3 text-lg w-full">
          <span className="text-gray-600">Already have an account?</span>
          <Link
            to="/signin"
            className="underline text-[#da0e1a] hover:text-[#c40d17] font-medium"
          >
            Login
          </Link>
        </div>
        <div id="recaptcha-container" ref={recaptchaRef}></div>
      </div>
    </div>
  );
}

export default Form;
